import React, { useEffect, useState } from "react";
import axios from "../api/baseurl";
import "react-toastify/dist/ReactToastify.css";
import { toast } from "react-toastify";
import { useNavigate } from "react-router";
import MapCompanyAndLabs from "./companyAndLabsMapping";
import { MdDeleteForever } from "react-icons/md";
import { Pagination } from "react-bootstrap";
import "./hm-theme.css";

function LinkedLabs({ selectedOrgcode }) {
  const [linkedlabs, setLinkedLabs] = useState([]);
  const [loader, setLoader] = useState(true);
  const [isMapCompanyAndLabs, setIsMapCompanyAndLabs] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const token = JSON.parse(localStorage.getItem("data"))?.token;

  useEffect(() => {
    if (searchTerm.trim()) {
      searchData(0);  
    } else {
      getData(currentPage);   
    }
  }, [searchTerm]); 
  
  const searchData = async (pageNumber = 0) => {
    setLoader(true);
    try {
      const searchUrl = `/api/company-labs-mapping/searchLinkedLabs`;
      const requestData = { searchTerm };
        
      const response = await axios.post(searchUrl, requestData, {
        params: { companyId: selectedCompanyId, page: pageNumber },
        headers: { Authorization: `Bearer ${token}` },
      });
      setLinkedLabs(response.data.content || []);
      setCurrentPage(response.data.number || 0);
      setTotalPages(response.data.totalPages || 0);
      setLoader(false);
    } catch (error) {
      setLoader(false);
      handleError(error);
    }
  };

  const getData = async (pageNumber = 0) => {
    setLoader(true);
    try {
      const compResponse = await axios.get(`/api/companydetails/companycode=${selectedOrgcode}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSelectedCompanyId(compResponse.data.id);

      const response = await axios.get(`/api/company-labs-mapping/getLinkedLabsByCompanyId/companyId=${compResponse.data.id}`, {
        params: { page: pageNumber },
        headers: { Authorization: `Bearer ${token}` },
      });

      setLinkedLabs(response.data.content || []);
      setCurrentPage(response.data.number || 0);
      setTotalPages(response.data.totalPages || 0);
      setLoader(false);
    } catch (error) {
      setLoader(false);
      handleError(error);
    }
  };

  const navigate = useNavigate();
  function handleError(error) {
    if (error.response) {
      if (error.response.status === 401) {
        navigate(`/signin`);
        toast.error("Your session has expired. Please login.");
      } else if (error.response.data && error.response.data.message) {
        toast.error(error.response.data.message);
      } else if (error.response.data && error.response.data.code) {
        toast.error(error.response.data.code);
      } else {
        toast.error("An error occurred.");
      }
    } else if (error.message) {
      toast.error(error.message);
    } else {
      toast.error("An error occurred.");
    }
  }

  const handleOpenMapPopup = () => {
    setIsMapCompanyAndLabs(true);
  };

  const handleCloseMapPopup = () => {
    setSelectedCompanyId(null);
    setIsMapCompanyAndLabs(false);
    getData();
  };

  const handleDeleteLabMapping = async (id) => {
    const token = JSON.parse(localStorage.getItem("data"))?.token;
    if (window.confirm("Are you sure you want to delete this mapping?")) {
      try {
        await axios.request({
          url: `/api/company-labs-mapping`,
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
          data: { id: id },
        });
    
        toast.success("Lab mapping removed successfully");
        getData();
      } catch (error) {
        handleError(error);
      }
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    if (searchTerm.trim()) {
      searchData(newPage);
    } else {
      getData(newPage);
    }
  };

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const maxPageNumbersToShow = 5;
    let startPage = Math.max(0, currentPage - 2);
    let endPage = Math.min(totalPages - 1, currentPage + 2);

    if (currentPage <= 2) endPage = Math.min(totalPages - 1, maxPageNumbersToShow - 1);
    if (currentPage >= totalPages - 3) startPage = Math.max(0, totalPages - maxPageNumbersToShow);

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <Pagination.Item key={i} active={i === currentPage} onClick={() => handlePageChange(i)}>
          {i + 1}
        </Pagination.Item>
      );
    }
    return pageNumbers;
  };

  return (
    <div>
      {loader && <div className="loading"></div>}

      <div className="hm-card">
        {/* Header */}
        <div className="hm-card-header">
          <h3 className="hm-card-title">
            <i className="material-icons">biotech</i>
            Linked Labs
          </h3>
          <button className="btn-hm-primary" onClick={handleOpenMapPopup}>
            <i className="material-icons" style={{ fontSize: "1rem" }}>link</i>
            Link Labs
          </button>
        </div>

        {/* Search */}
        <div className="hm-card-body border-bottom bg-light bg-opacity-50 p-3">
          <div className="row">
            <div className="col-md-6 col-lg-5">
              <div className="position-relative">
                <input
                  type="search"
                  className="form-control ps-4"
                  placeholder="Search by Lab name or Lab code..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <i className="material-icons position-absolute text-muted" style={{ left: "10px", top: "50%", transform: "translateY(-50%)", fontSize: "1.1rem" }}>
                  search
                </i>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="hm-card-body p-0">
          <div style={{ overflowX: "auto" }}>
            <table className="hm-table">
              <thead>
                <tr>
                  <th>Lab Name</th>
                  <th>Lab Code</th>
                  <th>Address</th>
                  <th style={{ width: "100px" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {linkedlabs.length === 0 ? (
                  <tr>
                    <td colSpan="4">
                      <div className="hm-empty-state">
                        <i className="material-icons">science_off</i>
                        <p>{loader ? "Loading labs..." : "No Data Available"}</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  linkedlabs.map((lab) => (
                    <tr key={lab.id}>
                      <td className="fw-semibold">{lab.labs?.labname || "—"}</td>
                      <td>
                        <span className="hm-badge hm-badge-primary">{lab.labs?.labcode || "—"}</span>
                      </td>
                      <td className="text-secondary small">
                        {lab.labs?.addressLine1}
                        {lab.labs?.addressLine2 && `, ${lab.labs.addressLine2}`}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm rounded-circle p-1 d-inline-flex align-items-center justify-content-center"
                          style={{ width: "32px", height: "32px" }}
                          title="Remove Mapped Lab"
                          onClick={() => handleDeleteLabMapping(lab.id)}
                        >
                          <MdDeleteForever size={18} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-center py-3">
              <Pagination className="hm-pagination">
                <Pagination.First onClick={() => handlePageChange(0)} disabled={currentPage === 0} />
                <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 0} />
                {renderPageNumbers()}
                <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage >= totalPages - 1} />
                <Pagination.Last onClick={() => handlePageChange(totalPages - 1)} disabled={currentPage >= totalPages - 1} />
              </Pagination>
            </div>
          )}
        </div>
      </div>

      {isMapCompanyAndLabs && (
        <MapCompanyAndLabs isOpen={isMapCompanyAndLabs} onRequestClose={handleCloseMapPopup} companyId={selectedCompanyId} />
      )}
    </div>
  );
}

export default LinkedLabs;

